import PagePreviousIcon from "@rsuite/icons/PagePrevious";
import { toaster, Notification } from "rsuite";

import { useSignupMutation } from "_services/auth";

import { Formik, Field, Form } from "formik";
import * as Yup from "yup";

import "./style.css";

import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router";

import { useGTMDispatch } from "@elgorditosalsero/react-gtm-hook";
import { useTranslation } from "react-i18next";

import { Helmet } from "react-helmet";
import { withBasePath } from "_config/basePath";

import { ReactComponent as Logo } from "ressources/icons/logo.svg";
import RolePicker from "components/advisor/RolePicker";
import { invitationEmail } from "components/advisor/invitations";
import AuthGoogleChoice from "components/advisor/AuthGoogleChoice";

const SignupSchema = Yup.object().shape({
    lastname: Yup.string().required("lastname-required"),
    firstname: Yup.string().required("firstname-required"),
    email: Yup.string().email("email-error").required("email-required"),
    password: Yup.string()
        .required("password-required")
        .min(8, "password-error"),
    confirmPassword: Yup.string()
        .required("please-confirm-password")
        .oneOf([Yup.ref("password"), null], "password-should-same"),
});

export default function Signup() {
    const [fromInvite, setFromInvite] = useState(false);
    const [signupWithMail, setSignupWithMail] = useState(false);
    const [userId, setUserId] = useState(null);
    const [role, setRole] = useState("student");

    const [signup, { isLoading }] = useSignupMutation();

    const navigate = useNavigate();

    const location = useLocation();

    const sendGTM = useGTMDispatch();
    const { t, i18n } = useTranslation();

    const user_id = new URLSearchParams(location.search).get("user_id");
    const invitedEmail = invitationEmail(location.search);

    useEffect(() => {
        if (user_id) {
            setUserId(user_id);
            setFromInvite(true);
        }
    }, []);

    return (
        <div className="signup bg-gradient-blue">
            <Helmet>
                <title>Thesis.center - {t("i-signup")}</title>
                <meta
                    name="description"
                    content="Inscris toi à Thesis pour profiter de notre algorithme de mise en forme automatique. Tu ne te serviras plus jamais de microsoft Word ou Pages!"
                />
            </Helmet>
            <div className="signup-back" onClick={() => navigate("/")}>
                <PagePreviousIcon
                    className="signup-back-icon"
                    style={{
                        cursor: "pointer",
                    }}
                />
                {t("back")}
            </div>
            <div className="signup-card">
                <div className="signup-card-header">{t("inscription")}</div>
                <RolePicker signup value={role} onChange={setRole} />
                <AuthGoogleChoice role={role}>
                {!signupWithMail && (
                    <div className="signup-card-alternate">
                        {/* <div className="signup-card-alternate-text">
            Créer un compte avec :
          </div> */}
                        <button
                            onClick={() => {
                                window.location.href =
                                    withBasePath("/api/oauth/google") +
                                    `?role=${role}` + (user_id ? "&user_id=" + encodeURIComponent(user_id) : "");
                            }}
                            className="login-card-google login-card-alternate-button"
                        >
                            <img width="15px" src={withBasePath("/google-logo.png")} alt="" />
                            {t("signup-with")} Google
                        </button>
                    </div>
                )}
                {!signupWithMail && (
                    <div className="login-card-or">
                        <div className="grey-line-left"></div>
                        <span>{t("or").toUpperCase()}</span>
                        <div className="grey-line-right"></div>
                    </div>
                )}
                </AuthGoogleChoice>
                {!signupWithMail && role !== "advisor" && (
                    <div className="signup-card-email-button-container">
                        <button
                            onClick={() => setSignupWithMail(true)}
                            className="signup-card-email-button"
                        >
                            {t("signup-with-email")}
                        </button>
                    </div>
                )}
                {(signupWithMail || role === "advisor") && (
                    <div className="signup-card-form">
                        <Formik
                            initialValues={{
                                lastname: "",
                                firstname: "",
                                school: "",
                                discipline: "",
                                email: invitedEmail,
                                password: "",
                                confirmPassword: "",
                            }}
                            validationSchema={SignupSchema}
                            onSubmit={async (values) => {
                                try {
                                    const payload = fromInvite
                                        ? { ...values, role, id: userId }
                                        : { ...values, role };
                                    await signup(payload).unwrap();
                                    sendGTM({
                                        event: "signUpDone",
                                        value: {
                                            method: "classic",
                                            invited: fromInvite,
                                        },
                                    });
                                    window.fathom?.trackGoal("CM6QIAE5", 0);
                                } catch (err) {
                                    const status = err?.status;
                                    const messageKey =
                                        status === 409
                                            ? "user-already-exists"
                                            : "signup-error";
                                    toaster.push(
                                        <Notification
                                            type="error"
                                            header={t("inscription")}
                                            closable
                                        >
                                            {t(messageKey)}
                                        </Notification>,
                                        { placement: "topEnd", duration: 5000 }
                                    );
                                }
                            }}
                        >
                            {({ errors, touched }) => (
                                <Form>
                                    <div className="signup-fields">
                                        <label>{t("firstname")}<Field id="firstname" name="firstname" autoComplete="given-name" />
                                            {errors.firstname && touched.firstname && <span className="form-validation-error">{t(errors.firstname)}</span>}
                                        </label>
                                        <label>{t("lastname")}<Field id="lastname" name="lastname" autoComplete="family-name" />
                                            {errors.lastname && touched.lastname && <span className="form-validation-error">{t(errors.lastname)}</span>}
                                        </label>
                                        <label className="aw-full-field">{t("email")}<Field id="email" name="email" type="email" autoComplete="email" />
                                            {errors.email && touched.email && <span className="form-validation-error">{t(errors.email)}</span>}
                                        </label>
                                        <label>{t("create-password")}<Field id="password" name="password" type="password" autoComplete="new-password" />
                                            {errors.password && touched.password && <span className="form-validation-error">{t(errors.password)}</span>}
                                        </label>
                                        <label>{t("confirm-password")}<Field id="confirmPassword" name="confirmPassword" type="password" autoComplete="new-password" />
                                            {errors.confirmPassword && touched.confirmPassword && <span className="form-validation-error">{t(errors.confirmPassword)}</span>}
                                        </label>
                                    </div>
                                    <button
                                        className="create-account-button"
                                        type="submit"
                                        disabled={isLoading}
                                    >
                                        {isLoading ? (i18n.language.startsWith("fr") ? "Création…" : "Creating…") : t("create-my-account")}
                                    </button>
                                </Form>
                            )}
                        </Formik>
                    </div>
                )}
                <div className="signup-card-footer">
                    <Logo className="login-card-logo" />
                </div>
            </div>
        </div>
    );
}
